<template>
  <v-row>
    <v-col>
      <v-card class="mb-3">
        <v-card-text class="d-flex flex-wrap justify-space-between align-center">
          <div>
            <v-btn size="small" variant="text" @click="router.push('/survey-forms')">Back to list</v-btn>
          </div>
          <div class="d-flex flex-wrap align-center">
            <v-btn size="small" variant="outlined" class="mr-2" @click="openFilters">Filters ({{ activeFilterCount(filters) }})</v-btn>
            <v-btn size="small" variant="outlined" class="mr-2" @click="openExclusions">Exclusions{{ hasExclusions(exclusions) ? ' (set)' : '' }}</v-btn>
            <v-btn
              v-if="isSurveyNotifyAvailable(form)"
              size="small"
              variant="outlined"
              prepend-icon="mdi-message"
              class="mr-2"
              @click="openSendMessage"
            >
              Send Message
            </v-btn>
            <v-btn size="small" variant="outlined" prepend-icon="mdi-eye" class="mr-2" @click="previewForm">Preview</v-btn>
            <v-btn size="small" color="primary" @click="saveSurvey">Save Form</v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-card class="mb-3">
        <v-card-text>
          <v-text-field label="Title" v-model="form.title" />
          <v-textarea label="Description" v-model="form.description" rows="2" />
          <v-row>
            <v-col cols="12" md="4">
              <VueDatePicker v-model="form.startDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="Start date" :teleport="true" />
            </v-col>
            <v-col cols="12" md="4">
              <VueDatePicker v-model="form.endDate" :enable-time-picker="false" auto-apply format="dd/MMM/yyyy" placeholder="End date" :teleport="true" />
            </v-col>
            <v-col cols="12" md="4" class="d-flex flex-column justify-center">
              <v-checkbox v-model="form.isActive" label="Active" density="compact" hide-details />
              <v-checkbox v-model="form.isAllowMultipleEntries" label="Allow multiple entries" density="compact" hide-details />
              <v-checkbox v-model="form.isAllowUniqueAnswers" label="Allow unique answers" density="compact" hide-details />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <draggable v-model="sections" item-key="key" handle=".section-drag" @end="onSectionReorder">
        <template #item="{ element: section, index: sIndex }">
          <v-card class="mb-3">
            <v-card-title class="d-flex align-center">
              <v-icon class="section-drag mr-2" style="cursor: grab">mdi-drag</v-icon>
              <v-text-field
                v-model="section.title"
                label="Section title"
                density="compact"
                hide-details
                class="flex-grow-1"
              />
              <v-btn icon="mdi-delete" size="small" variant="text" color="error" class="ml-2" @click="removeSection(sIndex)" />
            </v-card-title>
            <v-card-text>
              <draggable v-model="section.questions" item-key="key" handle=".question-drag" @end="recalcSortOrder">
                <template #item="{ element: question, index: qIndex }">
                  <v-card
                    variant="outlined"
                    class="mb-3 pa-3"
                    :class="{ 'question-inactive': question.isActive === false }"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon class="question-drag mr-2" style="cursor: grab">mdi-drag</v-icon>
                      <span class="text-subtitle-2">Question {{ qIndex + 1 }}</span>
                      <v-chip
                        v-if="question.isActive === false"
                        size="x-small"
                        color="warning"
                        class="ml-2"
                      >
                        Inactive
                      </v-chip>
                      <v-spacer />
                      <v-btn
                        v-if="question.isActive === false"
                        size="x-small"
                        variant="text"
                        color="primary"
                        class="mr-1"
                        @click="activateQuestion(question)"
                      >
                        Activate
                      </v-btn>
                      <v-btn icon="mdi-content-copy" size="x-small" variant="text" class="mr-1" @click="duplicateQuestion(section, question)" />
                      <v-btn
                        v-if="question.isActive !== false"
                        icon="mdi-delete"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="removeQuestion(section, qIndex)"
                      />
                    </div>
                    <v-text-field label="Question" v-model="question.question" :disabled="question.isActive === false" />
                    <v-textarea label="Description" v-model="question.description" rows="2" :disabled="question.isActive === false" />
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-select label="Question type" :items="QUESTION_TYPES" v-model="question.questionType" :disabled="question.isActive === false" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field label="Report header" v-model="question.reportHeader" :disabled="question.isActive === false" />
                      </v-col>
                    </v-row>
                    <v-checkbox v-model="question.isRequired" label="Required" density="compact" hide-details :disabled="question.isActive === false" />

                    <div v-if="needsOptions(question.questionType)" class="mt-3">
                      <div class="text-subtitle-2 mb-2">Options</div>
                      <div v-for="(opt, optIndex) in question.optionList" :key="`opt_${question.key}_${optIndex}`" class="d-flex mb-2">
                        <v-text-field v-model="question.optionList[optIndex]" density="compact" hide-details class="mr-2" :disabled="question.isActive === false" />
                        <v-btn icon="mdi-delete" size="x-small" variant="text" :disabled="question.isActive === false" @click="question.optionList.splice(optIndex, 1)" />
                      </div>
                      <v-btn size="small" variant="text" :disabled="question.isActive === false" @click="question.optionList.push('')">Add option</v-btn>
                    </div>

                    <div v-if="question.questionType === 'FileUpload'" class="mt-3">
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-select
                            label="Allowed file types"
                            :items="FILE_UPLOAD_PRESETS"
                            item-title="title"
                            item-value="value"
                            v-model="question.fileAcceptPreset"
                            density="compact"
                            hide-details
                            :disabled="question.isActive === false"
                          />
                        </v-col>
                        <v-col cols="12" md="6" v-if="question.fileAcceptPreset === 'custom'">
                          <v-text-field
                            label="Custom extensions"
                            v-model="question.fileAcceptCustom"
                            placeholder=".doc,.docx,.xls"
                            density="compact"
                            hint="Comma-separated extensions, e.g. .doc,.docx"
                            persistent-hint
                            :disabled="question.isActive === false"
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            label="Max file size (MB)"
                            v-model="question.maxFileSizeMb"
                            type="number"
                            min="1"
                            density="compact"
                            hint="Leave empty for no limit"
                            persistent-hint
                            :disabled="question.isActive === false"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-card>
                </template>
              </draggable>
              <div class="d-flex justify-end mt-2">
                <v-btn size="small" @click="addQuestion(section)">Add question</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </draggable>

      <div class="d-flex justify-end mb-6">
        <v-btn @click="addSection">Add section</v-btn>
      </div>
    </v-col>
  </v-row>

  <SurveyFiltersDialog ref="dlgFilters" v-model="filters" :survey-id="surveyId" />
  <SurveyExclusionsDialog ref="dlgExclusions" v-model="exclusions" :survey-id="surveyId" />
  <SurveyNotifyDialog ref="dlgNotify" :survey-id="surveyId" :survey-title="form.title" />
</template>

<script setup>
import { reactive, ref, inject, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import draggable from "vuedraggable";
import {
  getSurvey,
  updateSurvey,
  QUESTION_TYPES,
  FILE_UPLOAD_PRESETS,
  fileAcceptToPreset,
  presetToFileAccept,
  normalizeMaxFileSizeMb,
  isSurveyNotifyAvailable,
} from "@/services/surveyService";
import SurveyFiltersDialog from "./components/SurveyFiltersDialog.vue";
import SurveyExclusionsDialog from "./components/SurveyExclusionsDialog.vue";
import SurveyNotifyDialog from "./components/SurveyNotifyDialog.vue";
import {
  emptySurveyFilters,
  filtersToRows,
  rowsToFilters,
  activeFilterCount,
} from "./utils/surveyFilterUtils";
import {
  emptySurveyExclusions,
  exclusionsFromApi,
  hasExclusions,
  exclusionsToPayload,
} from "./utils/surveyExclusionUtils";

const route = useRoute();
const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");
const confirmDialog = inject("confirmDialog");

const surveyId = Number(route.params.id);
const dlgFilters = ref(null);
const dlgExclusions = ref(null);
const dlgNotify = ref(null);

let keyCounter = 1;
const nextKey = () => `k_${keyCounter++}`;

const form = reactive({
  title: "",
  description: "",
  startDate: null,
  endDate: null,
  isActive: true,
  isAllowMultipleEntries: false,
  isAllowUniqueAnswers: false,
});

const sections = ref([]);
const filters = ref(emptySurveyFilters());
const exclusions = ref(emptySurveyExclusions());

function needsOptions(type) {
  return ["Select", "MultiSelect", "Checkbox", "RadioGroup"].includes(type);
}

function createQuestion(data = {}) {
  const isFileUpload = (data.questionType ?? "Textbox") === "FileUpload";
  const optionList = !isFileUpload && data.options
    ? data.options.split(",").filter(Boolean)
    : [""];
  const fileAcceptPreset = isFileUpload ? fileAcceptToPreset(data.options) : "any";
  const fileAcceptCustom =
    isFileUpload && fileAcceptPreset === "custom" ? (data.options || "") : "";

  return {
    key: nextKey(),
    questionID: data.questionID ?? null,
    section: data.section ?? "",
    question: data.question ?? "",
    description: data.description ?? "",
    questionType: data.questionType ?? "Textbox",
    optionList: optionList.length ? optionList : [""],
    fileAcceptPreset,
    fileAcceptCustom,
    maxFileSizeMb: isFileUpload ? (normalizeMaxFileSizeMb(data.maxFileSizeMb) ?? "") : "",
    reportHeader: data.reportHeader ?? "",
    sortOrder: data.sortOrder ?? 0,
    isRequired: data.isRequired ?? false,
    isActive: data.isActive !== false,
  };
}

function groupQuestions(questions) {
  const map = new Map();
  (questions || []).forEach((q) => {
    const title = q.section?.trim() || "Default Section";
    if (!map.has(title)) map.set(title, []);
    map.get(title).push(createQuestion({
      questionID: q.questionID,
      section: title,
      question: q.question,
      description: q.description,
      questionType: q.questionType,
      options: q.options,
      maxFileSizeMb: q.maxFileSizeMb,
      reportHeader: q.reportHeader,
      sortOrder: q.sortOrder,
      isRequired: q.isRequired,
      isActive: q.isActive,
    }));
  });

  if (map.size === 0) {
    return [{ key: nextKey(), title: "Default Section", questions: [createQuestion()] }];
  }

  return Array.from(map.entries()).map(([title, qs]) => ({
    key: nextKey(),
    title,
    questions: qs.sort((a, b) => a.sortOrder - b.sortOrder),
  }));
}

function addSection() {
  sections.value.push({ key: nextKey(), title: `Section ${sections.value.length + 1}`, questions: [createQuestion()] });
}

function removeSection(index) {
  sections.value.splice(index, 1);
  if (sections.value.length === 0) addSection();
}

function addQuestion(section) {
  section.questions.push(createQuestion({ section: section.title }));
}

async function removeQuestion(section, index) {
  const question = section.questions[index];
  if (!question) return;

  // Unsaved questions can be removed immediately.
  if (!question.questionID) {
    section.questions.splice(index, 1);
    if (section.questions.length === 0) addQuestion(section);
    return;
  }

  const confirmed = await confirmDialog.confirm(
    "Deactivate question",
    "This question will be hidden from the form but will remain in reports. Continue?",
    "Yes",
    "No",
    true
  );
  if (!confirmed) return;

  question.isActive = false;
}

function activateQuestion(question) {
  question.isActive = true;
}

function questionOptionsForSave(q) {
  if (needsOptions(q.questionType)) {
    return q.optionList.filter(Boolean).join(",") || null;
  }
  if (q.questionType === "FileUpload") {
    return presetToFileAccept(q.fileAcceptPreset, q.fileAcceptCustom);
  }
  return null;
}

function questionMaxFileSizeForSave(q) {
  if (q.questionType !== "FileUpload") return null;
  return normalizeMaxFileSizeMb(q.maxFileSizeMb);
}

function duplicateQuestion(section, question) {
  const copy = createQuestion({
    ...question,
    questionID: null,
    options: questionOptionsForSave(question),
    maxFileSizeMb: questionMaxFileSizeForSave(question),
    section: section.title,
    isActive: true,
  });
  section.questions.push(copy);
}

function onSectionReorder() {
  recalcSortOrder();
}

function recalcSortOrder() {
  let order = 0;
  sections.value.forEach((section) => {
    section.questions.forEach((q) => {
      q.sortOrder = order++;
      q.section = section.title;
    });
  });
}

function flattenQuestions() {
  recalcSortOrder();
  const list = [];
  sections.value.forEach((section) => {
    section.questions.forEach((q) => {
      list.push({
        questionID: q.questionID,
        section: section.title,
        question: q.question,
        description: q.description,
        questionType: q.questionType,
        options: questionOptionsForSave(q),
        maxFileSizeMb: questionMaxFileSizeForSave(q),
        reportHeader: q.reportHeader,
        sortOrder: q.sortOrder,
        isRequired: q.isRequired,
        isActive: q.isActive !== false,
      });
    });
  });
  return list;
}

function buildPayload() {
  return {
    title: form.title,
    description: form.description,
    startDate: form.startDate,
    endDate: form.endDate,
    isActive: form.isActive,
    isAllowMultipleEntries: form.isAllowMultipleEntries,
    isAllowUniqueAnswers: form.isAllowUniqueAnswers,
    questions: flattenQuestions(),
    filters: filtersToRows(filters.value),
    ...exclusionsToPayload(exclusions.value),
  };
}

async function loadSurvey() {
  loader.show();
  try {
    const data = await getSurvey(surveyId);
    form.title = data.title;
    form.description = data.description;
    form.startDate = data.startDate;
    form.endDate = data.endDate;
    form.isActive = data.isActive;
    form.isAllowMultipleEntries = data.isAllowMultipleEntries;
    form.isAllowUniqueAnswers = data.isAllowUniqueAnswers;
    sections.value = groupQuestions(data.questions);
    filters.value = rowsToFilters(data.filters);
    exclusions.value = exclusionsFromApi(data);
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function saveSurvey() {
  if (!form.title?.trim()) {
    snackbar.error("Title is required");
    return;
  }

  loader.show();
  try {
    await updateSurvey(surveyId, buildPayload());
    snackbar.success("Survey saved");
    await loadSurvey();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

function openFilters() {
  dlgFilters.value.show();
}

function openExclusions() {
  dlgExclusions.value.show();
}

function openSendMessage() {
  dlgNotify.value.show();
}

function previewForm() {
  const url = router.resolve({ path: `/survey-forms/${surveyId}`, query: { preview: "1" } }).href;
  window.open(url, "_blank");
}

onMounted(loadSurvey);
</script>

<style scoped>
.question-inactive {
  opacity: 0.65;
  background-color: rgba(0, 0, 0, 0.03);
}
</style>
